import { api } from "./api";
import type {
  AdjustConnectRequest,
  AdjustConnectResponse,
  AdjustDisconnectResponse,
  AdjustPairedAvailabilityResponse,
  AdjustReusePairedRequest,
  AdjustReusePairedResponse,
  AdjustStatusResponse,
  AppleSearchAdsConnectRequest,
  AppleSearchAdsConnectResponse,
  AppleSearchAdsDisconnectResponse,
  AppleSearchAdsPairedAvailabilityResponse,
  AppleSearchAdsReusePairedResponse,
  AppleSearchAdsStatusResponse,
  AppsFlyerConnectRequest,
  AppsFlyerConnectResponse,
  AppsFlyerDisconnectResponse,
  AppsFlyerPairedAvailabilityResponse,
  AppsFlyerReusePairedRequest,
  AppsFlyerReusePairedResponse,
  AppsFlyerSetPaymentEventRequest,
  AppsFlyerSetPaymentEventResponse,
  AppsFlyerStatusResponse,
  AppsFlyerSyncRequest,
  AppsFlyerSyncResponse,
  AscAnalyticsConnectRequest,
  AscAnalyticsConnectResponse,
  AscAnalyticsDisconnectResponse,
  AscAnalyticsPairedAvailabilityResponse,
  AscAnalyticsReusePairedResponse,
  AscAnalyticsSetPaymentActionRequest,
  AscAnalyticsSetPaymentActionResponse,
  AscAnalyticsSetVendorNumberRequest,
  AscAnalyticsSetVendorNumberResponse,
  AscAnalyticsStatusResponse,
  AscAnalyticsVerifySalesReportsResponse,
  AscSessionConnectRequest,
  AscSessionConnectResponse,
  AscSessionDisconnectResponse,
  AscSessionPairedAvailabilityResponse,
  AscSessionReusePairedResponse,
  AscSessionStatusResponse,
  BranchConnectRequest,
  BranchConnectResponse,
  BranchDisconnectResponse,
  BranchPairedAvailabilityResponse,
  BranchReusePairedRequest,
  BranchReusePairedResponse,
  BranchStatusResponse,
  ConnectionStateName,
  ConnectorReusePairedRequest,
  FirebaseConnectRequest,
  FirebaseConnectResponse,
  FirebaseDisconnectResponse,
  FirebaseEventsResponse,
  FirebasePairedAvailabilityResponse,
  FirebaseReusePairedResponse,
  FirebaseSetPaymentEventRequest,
  FirebaseSetPaymentEventResponse,
  FirebaseStatusResponse,
  GoogleAdsConnectRequest,
  GoogleAdsConnectResponse,
  GoogleAdsConversionActionsResponse,
  GoogleAdsDisconnectResponse,
  GoogleAdsDiscoverRequest,
  GoogleAdsDiscoverResponse,
  GoogleAdsOAuthAuthorizeResponse,
  GoogleAdsPairedAvailabilityResponse,
  GoogleAdsReusePairedRequest,
  GoogleAdsReusePairedResponse,
  GoogleAdsSelectAccountsRequest,
  GoogleAdsSelectAccountsResponse,
  GoogleAdsSetPaymentActionRequest,
  GoogleAdsSetPaymentActionResponse,
  GoogleAdsStatusResponse,
  IntegrationSchemaValidateRequest,
  IntegrationSchemaValidateResponse,
  IntegrationsStatusSummaryResponse,
  IntegrationStateResponse,
  PlayConsoleConnectRequest,
  PlayConsoleConnectResponse,
  PlayConsoleDisconnectResponse,
  PlayConsoleOAuthAuthorizeResponse,
  PlayConsolePairedAvailabilityResponse,
  PlayConsoleReusePairedResponse,
  PlayConsoleSessionConnectRequest,
  PlayConsoleSessionConnectResponse,
  PlayConsoleSessionDisconnectResponse,
  PlayConsoleSessionPairedAvailabilityResponse,
  PlayConsoleSessionReusePairedResponse,
  PlayConsoleSessionStatusResponse,
  PlayConsoleStatusResponse,
  ProviderName,
  RevenueProjectionInputsResponse,
} from "@/types";

// Integration setup — connection-state inspector + schema-mapping validator
// (BRIEF_integration-setup-and-ops-observability-http-surface §8.A). Both
// endpoints are stateless; no Idempotency-Key required.

export async function integrationState(
  provider: ProviderName,
  appId: string,
  state: ConnectionStateName,
  proposedTo?: ConnectionStateName,
): Promise<IntegrationStateResponse> {
  const params = new URLSearchParams({ state });
  if (proposedTo) params.set("proposed_to", proposedTo);
  const { data } = await api.get<IntegrationStateResponse>(
    `/api/v1/integrations/${provider}/state/${appId}?${params.toString()}`,
  );
  return data;
}

export async function integrationSchemaValidate(
  provider: ProviderName,
  payload: IntegrationSchemaValidateRequest,
): Promise<IntegrationSchemaValidateResponse> {
  const { data } = await api.post<IntegrationSchemaValidateResponse>(
    `/api/v1/integrations/${provider}/schema/validate`,
    payload,
  );
  return data;
}

// AppsFlyer V1 connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track C).
// Per-app + explicit pair-reuse; routes extracted to integrations_appsflyer.py.

export async function appsflyerConnect(
  payload: AppsFlyerConnectRequest,
): Promise<AppsFlyerConnectResponse> {
  const { data } = await api.post<AppsFlyerConnectResponse>(
    "/api/v1/integrations/appsflyer",
    payload,
  );
  return data;
}

export async function appsflyerSync(
  payload: AppsFlyerSyncRequest,
): Promise<AppsFlyerSyncResponse> {
  const { data } = await api.post<AppsFlyerSyncResponse>(
    "/api/v1/integrations/appsflyer/sync",
    payload,
  );
  return data;
}

export async function appsflyerStatus(
  appId: string,
): Promise<AppsFlyerStatusResponse> {
  const { data } = await api.get<AppsFlyerStatusResponse>(
    `/api/v1/integrations/appsflyer/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function appsflyerReusePaired(
  appId: string,
  payload: AppsFlyerReusePairedRequest,
): Promise<AppsFlyerReusePairedResponse> {
  const { data } = await api.post<AppsFlyerReusePairedResponse>(
    `/api/v1/integrations/appsflyer/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function appsflyerPairedAvailability(
  appId: string,
): Promise<AppsFlyerPairedAvailabilityResponse> {
  const { data } = await api.get<AppsFlyerPairedAvailabilityResponse>(
    `/api/v1/integrations/appsflyer/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

// AppsFlyer Payment-event selector (ENG-APPSFLYER-PAYMENT-CONVERSION).
// AppsFlyer exposes NO in-app-events enumeration API, so the operator
// types/pastes the Payment event_name (free-text). PUT
// .../{app_id}/payment-event-name persists it (§ Conversion-action selection);
// consumed by the Phase-2 cohort writer (M7).

export async function appsflyerSetPaymentEvent(
  appId: string,
  payload: AppsFlyerSetPaymentEventRequest,
): Promise<AppsFlyerSetPaymentEventResponse> {
  const { data } = await api.put<AppsFlyerSetPaymentEventResponse>(
    `/api/v1/integrations/appsflyer/${encodeURIComponent(appId)}/payment-event-name`,
    payload,
  );
  return data;
}

// AppsFlyer disconnect — removes the app's credentials row, emits
// `integration_disconnected` audit event server-side. Idempotent: 200
// with `disconnected=false` when no row was present.

export async function appsflyerDisconnect(
  appId: string,
): Promise<AppsFlyerDisconnectResponse> {
  const { data } = await api.delete<AppsFlyerDisconnectResponse>(
    `/api/v1/integrations/appsflyer/${encodeURIComponent(appId)}`,
  );
  return data;
}

// AppsFlyer V1 revenue-projection inputs (Direction #41 contribution writer).
// Backed by HTTP_API.md endpoint 88. Returns ARPU + install-to-paid rate +
// lifespan + freshness for the consumer-side revenue-band UX.

// Adjust V1.1 MMP connector (ENG-FE-PHASE-2-WAVE-1-F4-ADJUST).
// Three endpoints — connect (verifies via /reports-service/report probe +
// persists) / disconnect (idempotent) / status. NEVER carries the api_token
// in the response.

export async function adjustConnect(
  payload: AdjustConnectRequest,
): Promise<AdjustConnectResponse> {
  const { data } = await api.post<AdjustConnectResponse>(
    "/api/v1/integrations/adjust",
    payload,
  );
  return data;
}

export async function adjustDisconnect(
  appId: string,
): Promise<AdjustDisconnectResponse> {
  const { data } = await api.delete<AdjustDisconnectResponse>(
    `/api/v1/integrations/adjust/${encodeURIComponent(appId)}`,
  );
  return data;
}

export async function adjustStatus(
  appId: string,
): Promise<AdjustStatusResponse> {
  const { data } = await api.get<AdjustStatusResponse>(
    `/api/v1/integrations/adjust/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function adjustReusePaired(
  appId: string,
  payload: AdjustReusePairedRequest,
): Promise<AdjustReusePairedResponse> {
  const { data } = await api.post<AdjustReusePairedResponse>(
    `/api/v1/integrations/adjust/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function adjustPairedAvailability(
  appId: string,
): Promise<AdjustPairedAvailabilityResponse> {
  const { data } = await api.get<AdjustPairedAvailabilityResponse>(
    `/api/v1/integrations/adjust/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

// Branch.io V1.1 MMP connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track C).
// Five per-app endpoints — connect (verifies via Query API probe + persists) /
// reuse-paired / paired-availability / disconnect (idempotent) / status.
// Two-secret auth model (branch_key + branch_secret) — both Fernet-encrypted
// server-side. NEVER carries either secret in the response.

export async function branchConnect(
  payload: BranchConnectRequest,
): Promise<BranchConnectResponse> {
  const { data } = await api.post<BranchConnectResponse>(
    "/api/v1/integrations/branch",
    payload,
  );
  return data;
}

export async function branchDisconnect(
  appId: string,
): Promise<BranchDisconnectResponse> {
  const { data } = await api.delete<BranchDisconnectResponse>(
    `/api/v1/integrations/branch/${encodeURIComponent(appId)}`,
  );
  return data;
}

export async function branchStatus(
  appId: string,
): Promise<BranchStatusResponse> {
  const { data } = await api.get<BranchStatusResponse>(
    `/api/v1/integrations/branch/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function branchReusePaired(
  appId: string,
  payload: BranchReusePairedRequest,
): Promise<BranchReusePairedResponse> {
  const { data } = await api.post<BranchReusePairedResponse>(
    `/api/v1/integrations/branch/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function branchPairedAvailability(
  appId: string,
): Promise<BranchPairedAvailabilityResponse> {
  const { data } = await api.get<BranchPairedAvailabilityResponse>(
    `/api/v1/integrations/branch/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

// Firebase Analytics MMP connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track B).
// Seven per-app endpoints — connect / reuse-paired / paired-availability /
// disconnect / status / events / payment-event. GCP service-account JSON auth;
// encrypted at rest. NEVER carries the JSON or its ciphertext in any response.

export async function firebaseConnect(
  payload: FirebaseConnectRequest,
): Promise<FirebaseConnectResponse> {
  const { data } = await api.post<FirebaseConnectResponse>(
    "/api/v1/integrations/firebase",
    payload,
  );
  return data;
}

export async function firebaseReusePaired(
  appId: string,
  payload: ConnectorReusePairedRequest,
): Promise<FirebaseReusePairedResponse> {
  const { data } = await api.post<FirebaseReusePairedResponse>(
    `/api/v1/integrations/firebase/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function firebasePairedAvailability(
  appId: string,
): Promise<FirebasePairedAvailabilityResponse> {
  const { data } = await api.get<FirebasePairedAvailabilityResponse>(
    `/api/v1/integrations/firebase/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function firebaseDisconnect(
  appId: string,
): Promise<FirebaseDisconnectResponse> {
  const { data } = await api.delete<FirebaseDisconnectResponse>(
    `/api/v1/integrations/firebase/${encodeURIComponent(appId)}`,
  );
  return data;
}

export async function firebaseStatus(
  appId: string,
): Promise<FirebaseStatusResponse> {
  const { data } = await api.get<FirebaseStatusResponse>(
    `/api/v1/integrations/firebase/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

// Firebase Payment-event selector (§ Conversion-action selection).
// GET .../events enumerates the connected project's distinct event_names
// (a live BigQuery fetch) + Payment candidates; PUT .../payment-event
// persists the operator's choice.

export async function firebaseEvents(
  appId: string,
): Promise<FirebaseEventsResponse> {
  const { data } = await api.get<FirebaseEventsResponse>(
    `/api/v1/integrations/firebase/${encodeURIComponent(appId)}/events`,
  );
  return data;
}

export async function firebaseSetPaymentEvent(
  appId: string,
  payload: FirebaseSetPaymentEventRequest,
): Promise<FirebaseSetPaymentEventResponse> {
  const { data } = await api.put<FirebaseSetPaymentEventResponse>(
    `/api/v1/integrations/firebase/${encodeURIComponent(appId)}/payment-event`,
    payload,
  );
  return data;
}

// Apple Search Ads connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track A).
// Five per-app endpoints — connect / reuse-paired / paired-availability /
// disconnect / status. NEVER carries the .p8 PEM in the response or the OAuth
// access token in any direction.

export async function appleSearchAdsConnect(
  payload: AppleSearchAdsConnectRequest,
): Promise<AppleSearchAdsConnectResponse> {
  const { data } = await api.post<AppleSearchAdsConnectResponse>(
    "/api/v1/integrations/apple-search-ads",
    payload,
  );
  return data;
}

export async function appleSearchAdsReusePaired(
  appId: string,
  payload: ConnectorReusePairedRequest,
): Promise<AppleSearchAdsReusePairedResponse> {
  const { data } = await api.post<AppleSearchAdsReusePairedResponse>(
    `/api/v1/integrations/apple-search-ads/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function appleSearchAdsPairedAvailability(
  appId: string,
): Promise<AppleSearchAdsPairedAvailabilityResponse> {
  const { data } = await api.get<AppleSearchAdsPairedAvailabilityResponse>(
    `/api/v1/integrations/apple-search-ads/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function appleSearchAdsStatus(
  appId: string,
): Promise<AppleSearchAdsStatusResponse> {
  const { data } = await api.get<AppleSearchAdsStatusResponse>(
    `/api/v1/integrations/apple-search-ads/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function appleSearchAdsDisconnect(
  appId: string,
): Promise<AppleSearchAdsDisconnectResponse> {
  const { data } = await api.delete<AppleSearchAdsDisconnectResponse>(
    `/api/v1/integrations/apple-search-ads/${encodeURIComponent(appId)}`,
  );
  return data;
}

// Google Ads connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Sub-1; subsumes
// ENG-C6-GADS Sub-3 + ENG-GADS-MCC-ACCOUNT-DISCOVERY). Eight per-app endpoints
// — connect / discover-accounts / reuse-paired / paired-availability /
// disconnect / status + the Payment-conversion selector (list
// conversion-actions / set payment-conversion-action). Every endpoint is keyed
// by `app_id`. NEVER carries the OAuth secrets (client_secret / refresh_token /
// developer_token) in any direction.

export async function googleAdsConnect(
  payload: GoogleAdsConnectRequest,
): Promise<GoogleAdsConnectResponse> {
  const { data } = await api.post<GoogleAdsConnectResponse>(
    "/api/v1/integrations/google-ads",
    payload,
  );
  return data;
}

export async function googleAdsDiscoverAccounts(
  payload: GoogleAdsDiscoverRequest,
): Promise<GoogleAdsDiscoverResponse> {
  const { data } = await api.post<GoogleAdsDiscoverResponse>(
    "/api/v1/integrations/google-ads/discover-accounts",
    payload,
  );
  return data;
}

export async function googleAdsDiscoverConnected(
  appId: string,
): Promise<GoogleAdsDiscoverResponse> {
  const { data } = await api.get<GoogleAdsDiscoverResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/discover-connected`,
  );
  return data;
}

export async function googleAdsSelectAccounts(
  appId: string,
  payload: GoogleAdsSelectAccountsRequest,
): Promise<GoogleAdsSelectAccountsResponse> {
  const { data } = await api.put<GoogleAdsSelectAccountsResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/accounts`,
    payload,
  );
  return data;
}

export async function googleAdsReusePaired(
  appId: string,
  payload: GoogleAdsReusePairedRequest,
): Promise<GoogleAdsReusePairedResponse> {
  const { data } = await api.post<GoogleAdsReusePairedResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function googleAdsPairedAvailability(
  appId: string,
): Promise<GoogleAdsPairedAvailabilityResponse> {
  const { data } = await api.get<GoogleAdsPairedAvailabilityResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function googleAdsDisconnect(
  appId: string,
): Promise<GoogleAdsDisconnectResponse> {
  const { data } = await api.delete<GoogleAdsDisconnectResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}`,
  );
  return data;
}

export async function googleAdsStatus(
  appId: string,
): Promise<GoogleAdsStatusResponse> {
  const { data } = await api.get<GoogleAdsStatusResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function googleAdsConversionActions(
  appId: string,
): Promise<GoogleAdsConversionActionsResponse> {
  const { data } = await api.get<GoogleAdsConversionActionsResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/conversion-actions`,
  );
  return data;
}

export async function googleAdsSetPaymentAction(
  appId: string,
  payload: GoogleAdsSetPaymentActionRequest,
): Promise<GoogleAdsSetPaymentActionResponse> {
  const { data } = await api.put<GoogleAdsSetPaymentActionResponse>(
    `/api/v1/integrations/google-ads/${encodeURIComponent(appId)}/payment-conversion-action`,
    payload,
  );
  return data;
}

// ASC Analytics connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track A).
// Eight per-app endpoints — connect / reuse-paired / paired-availability /
// payment-conversion-action / vendor-number / verify-sales-reports /
// disconnect / status. NEVER carries the .p8 PEM in the response or any
// ciphertext in either direction. (This count was stale at "Five" over six
// functions before 2026-09-09; it is hand-maintained.)

export async function ascAnalyticsConnect(
  payload: AscAnalyticsConnectRequest,
): Promise<AscAnalyticsConnectResponse> {
  const { data } = await api.post<AscAnalyticsConnectResponse>(
    "/api/v1/integrations/asc-analytics",
    payload,
  );
  return data;
}

export async function ascAnalyticsReusePaired(
  appId: string,
  payload: ConnectorReusePairedRequest,
): Promise<AscAnalyticsReusePairedResponse> {
  const { data } = await api.post<AscAnalyticsReusePairedResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function ascAnalyticsPairedAvailability(
  appId: string,
): Promise<AscAnalyticsPairedAvailabilityResponse> {
  const { data } = await api.get<AscAnalyticsPairedAvailabilityResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function ascAnalyticsStatus(
  appId: string,
): Promise<AscAnalyticsStatusResponse> {
  const { data } = await api.get<AscAnalyticsStatusResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function ascAnalyticsDisconnect(
  appId: string,
): Promise<AscAnalyticsDisconnectResponse> {
  const { data } = await api.delete<AscAnalyticsDisconnectResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}`,
  );
  return data;
}

/**
 * PUT /api/v1/integrations/asc-analytics/{app_id}/payment-conversion-action —
 * designate the operator's Payment conversion event. Enables the payment-CVR
 * (locale Trigger 2) signal for the ASC feed on the next sync. Mirrors the
 * Google Ads payment-conversion selector.
 */
export async function ascAnalyticsSetPaymentAction(
  appId: string,
  payload: AscAnalyticsSetPaymentActionRequest,
): Promise<AscAnalyticsSetPaymentActionResponse> {
  const { data } = await api.put<AscAnalyticsSetPaymentActionResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}/payment-conversion-action`,
    payload,
  );
  return data;
}

/**
 * PUT /api/v1/integrations/asc-analytics/{app_id}/vendor-number — store the
 * Apple vendor number that gates the Sales and Trends download feed.
 *
 * `GET /v1/salesReports` is the Apple report endpoint ASOLoop reads: the
 * regular stored key pulls daily download statistics with it. Apple's
 * Analytics Reports family is not an ASOLoop data source and no report request
 * exists for the app. Apple returns the vendor number from no API endpoint —
 * the operator reads it in App Store Connect → Reports, top-left under the
 * Legal Entity Name.
 */
export async function ascAnalyticsSetVendorNumber(
  appId: string,
  payload: AscAnalyticsSetVendorNumberRequest,
): Promise<AscAnalyticsSetVendorNumberResponse> {
  const { data } = await api.put<AscAnalyticsSetVendorNumberResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}/vendor-number`,
    payload,
  );
  return data;
}

/**
 * POST /api/v1/integrations/asc-analytics/{app_id}/verify-sales-reports —
 * one read-only GET against Apple, reporting whether the feed is reachable.
 *
 * Returns no report content: only the header row's column names and a row
 * count. `reachable: true` with `rowCount: 0` is a SUCCESS — a day with no
 * sales is a real answer — and must not be rendered as a failure.
 */
export async function ascAnalyticsVerifySalesReports(
  appId: string,
): Promise<AscAnalyticsVerifySalesReportsResponse> {
  const { data } = await api.post<AscAnalyticsVerifySalesReportsResponse>(
    `/api/v1/integrations/asc-analytics/${encodeURIComponent(appId)}/verify-sales-reports`,
  );
  return data;
}

// Play Console connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track B).
// Five per-app endpoints — connect / reuse-paired / paired-availability /
// disconnect / status. NEVER carries the service-account JSON, ciphertext, or
// signing key in either direction.

export async function playConsoleConnect(
  payload: PlayConsoleConnectRequest,
): Promise<PlayConsoleConnectResponse> {
  const { data } = await api.post<PlayConsoleConnectResponse>(
    "/api/v1/integrations/play-console",
    payload,
  );
  return data;
}

export async function playConsoleReusePaired(
  appId: string,
  payload: ConnectorReusePairedRequest,
): Promise<PlayConsoleReusePairedResponse> {
  const { data } = await api.post<PlayConsoleReusePairedResponse>(
    `/api/v1/integrations/play-console/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function playConsolePairedAvailability(
  appId: string,
): Promise<PlayConsolePairedAvailabilityResponse> {
  const { data } = await api.get<PlayConsolePairedAvailabilityResponse>(
    `/api/v1/integrations/play-console/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function playConsoleStatus(
  appId: string,
): Promise<PlayConsoleStatusResponse> {
  const { data } = await api.get<PlayConsoleStatusResponse>(
    `/api/v1/integrations/play-console/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function playConsoleDisconnect(
  appId: string,
): Promise<PlayConsoleDisconnectResponse> {
  const { data } = await api.delete<PlayConsoleDisconnectResponse>(
    `/api/v1/integrations/play-console/${encodeURIComponent(appId)}`,
  );
  return data;
}

// Play Console 3-legged OAuth connect (ENG-PLAY-OAUTH-CONNECT sub-6).
// GET .../oauth/authorize returns the Google consent URL; the card then sets
// `window.location` to it. app_id + developer_account_id ride as query params —
// the developer account id determines the pubsite_prod_rev_<id> GCS bucket and
// Google's consent does not return it, so the operator supplies it here and it
// rides the signed CSRF state to the callback. NEVER carries a secret.

export async function googleAdsOAuthAuthorize(
  appId: string,
): Promise<GoogleAdsOAuthAuthorizeResponse> {
  const params = new URLSearchParams({ app_id: appId });
  const { data } = await api.get<GoogleAdsOAuthAuthorizeResponse>(
    `/api/v1/integrations/google-ads/oauth/authorize?${params.toString()}`,
  );
  return data;
}

// `developerAccountId` is OPTIONAL: omitted, the server resolves it from the
// workspace's existing Play Console session / API credential, which is what
// makes the connect one click (matching googleAdsOAuthAuthorize above). Pass it
// only after the server answered 400 `developer_account_id_required`.
export async function playConsoleOAuthAuthorize(
  appId: string,
  developerAccountId?: string,
): Promise<PlayConsoleOAuthAuthorizeResponse> {
  const params = new URLSearchParams({ app_id: appId });
  if (developerAccountId) {
    params.set("developer_account_id", developerAccountId);
  }
  const { data } = await api.get<PlayConsoleOAuthAuthorizeResponse>(
    `/api/v1/integrations/play-console/oauth/authorize?${params.toString()}`,
  );
  return data;
}

// Play Console session connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track B).
// Five per-app endpoints — connect / reuse-paired / paired-availability /
// disconnect / status. NEVER carries the session_blob, cookies, SAPISID, or
// ciphertext in either direction. DISTINCT from playConsole* (Reports API
// service-account JSON); the session surface feeds the Playwright organic-keyword
// scrape worker.

export async function playConsoleSessionConnect(
  payload: PlayConsoleSessionConnectRequest,
): Promise<PlayConsoleSessionConnectResponse> {
  const { data } = await api.post<PlayConsoleSessionConnectResponse>(
    "/api/v1/integrations/play-console-session",
    payload,
  );
  return data;
}

export async function playConsoleSessionReusePaired(
  appId: string,
  payload: ConnectorReusePairedRequest,
): Promise<PlayConsoleSessionReusePairedResponse> {
  const { data } = await api.post<PlayConsoleSessionReusePairedResponse>(
    `/api/v1/integrations/play-console-session/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function playConsoleSessionPairedAvailability(
  appId: string,
): Promise<PlayConsoleSessionPairedAvailabilityResponse> {
  const { data } = await api.get<PlayConsoleSessionPairedAvailabilityResponse>(
    `/api/v1/integrations/play-console-session/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function playConsoleSessionStatus(
  appId: string,
): Promise<PlayConsoleSessionStatusResponse> {
  const { data } = await api.get<PlayConsoleSessionStatusResponse>(
    `/api/v1/integrations/play-console-session/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

export async function playConsoleSessionDisconnect(
  appId: string,
): Promise<PlayConsoleSessionDisconnectResponse> {
  const { data } = await api.delete<PlayConsoleSessionDisconnectResponse>(
    `/api/v1/integrations/play-console-session/${encodeURIComponent(appId)}`,
  );
  return data;
}

// ASC session connector (ENG-INTEGRATIONS-PER-APP-PAIR-SCOPING Track A).
// Five per-app endpoints — connect / reuse-paired / paired-availability /
// disconnect / status. NEVER carries the session_blob, cookies, or ciphertext
// in either direction. DISTINCT from ascAnalytics* (JWT Reports API
// service-account); the session surface feeds the Playwright Apple PPO
// Analytics XHR-intercept scrape worker.

export async function ascSessionConnect(
  payload: AscSessionConnectRequest,
): Promise<AscSessionConnectResponse> {
  const { data } = await api.post<AscSessionConnectResponse>(
    "/api/v1/integrations/asc-session",
    payload,
  );
  return data;
}

export async function ascSessionReusePaired(
  appId: string,
  payload: ConnectorReusePairedRequest,
): Promise<AscSessionReusePairedResponse> {
  const { data } = await api.post<AscSessionReusePairedResponse>(
    `/api/v1/integrations/asc-session/${encodeURIComponent(appId)}/reuse-paired`,
    payload,
  );
  return data;
}

export async function ascSessionPairedAvailability(
  appId: string,
): Promise<AscSessionPairedAvailabilityResponse> {
  const { data } = await api.get<AscSessionPairedAvailabilityResponse>(
    `/api/v1/integrations/asc-session/${encodeURIComponent(appId)}/paired-availability`,
  );
  return data;
}

export async function ascSessionStatus(
  appId: string,
): Promise<AscSessionStatusResponse> {
  const { data } = await api.get<AscSessionStatusResponse>(
    `/api/v1/integrations/asc-session/${encodeURIComponent(appId)}/status`,
  );
  return data;
}

/**
 * Every integration's status for an app in ONE round trip — the app-shell
 * cards' read (ENG-APP-UX-TUNEUP floor row 11). The settings pages keep the
 * per-integration `…Status` fetchers above.
 */
export async function integrationsStatusSummary(
  appId: string,
): Promise<IntegrationsStatusSummaryResponse> {
  const { data } = await api.get<IntegrationsStatusSummaryResponse>(
    `/api/v1/integrations/${encodeURIComponent(appId)}/status-summary`,
  );
  return data;
}

export async function ascSessionDisconnect(
  appId: string,
): Promise<AscSessionDisconnectResponse> {
  const { data } = await api.delete<AscSessionDisconnectResponse>(
    `/api/v1/integrations/asc-session/${encodeURIComponent(appId)}`,
  );
  return data;
}

export async function appsflyerRevenueProjectionInputs(
  appId: string,
  options?: { windowDays?: number },
): Promise<RevenueProjectionInputsResponse> {
  const params = new URLSearchParams();
  if (options?.windowDays !== undefined) {
    params.set("window_days", String(options.windowDays));
  }
  const query = params.toString();
  const suffix = query ? `?${query}` : "";
  const { data } = await api.get<RevenueProjectionInputsResponse>(
    `/api/v1/integrations/appsflyer/${encodeURIComponent(appId)}/revenue-projection-inputs${suffix}`,
  );
  return data;
}
